"use client";

import { useEffect, useRef, useState } from "react";
import { useKeyboardHeight } from "./useKeyboardHeight";

const WEB_KEYBOARD_THRESHOLD = 80;

/**
 * Geometry for a full-screen `position: fixed` chat overlay that must hug the
 * soft keyboard. Returns:
 *
 * - `height`: the on-screen area not covered by the keyboard. Starts from
 *   `visualViewport.height` and additionally subtracts the part of the native
 *   keyboard (Capacitor Keyboard plugin) that the visual viewport has NOT
 *   already accounted for. Some WebViews shrink vv.height when the keyboard
 *   opens (iOS Safari/PWA, Android with interactive-widget=resizes-content)
 *   and some do not (iOS WKWebView inside Capacitor) — comparing against the
 *   keyboard-closed baseline handles both without platform branching.
 * - `offsetTop`: `visualViewport.offsetTop`. iOS scrolls the viewport to
 *   reveal the focused input *before* the overlay has shrunk, which shoves a
 *   fixed overlay off-screen; callers compensate with
 *   `transform: translateY(offsetTop)`.
 * - `safeTop`: `env(safe-area-inset-top)` measured (via a probe element)
 *   while the keyboard is CLOSED, and frozen while it is open. iOS WKWebView
 *   collapses the env() insets while the keyboard is up, which silently
 *   strips a `pt-safe` class and lets the header slide under the status bar;
 *   callers apply this value as an inline padding-top instead. `null` before
 *   the first measurement (callers fall back to the pt-safe class).
 *
 * Keyboard-open detection on the web compares vv.height against the largest
 * height seen (reset on orientation change) rather than window.innerHeight —
 * with `interactive-widget=resizes-content` the layout viewport shrinks
 * together with the visual viewport, so an innerHeight delta reads 0 there.
 *
 * While the keyboard is open the hook also pins `window.scrollTo(0, 0)` —
 * the overlay keeps the input visible by itself, so any browser-initiated
 * reveal scroll only misaligns fixed positioning.
 *
 * `height` is 0 before the first measurement (SSR included); callers fall
 * back to `100dvh`. Pass `enabled: false` while the overlay is closed so a
 * keyboard opened elsewhere on the page isn't fought.
 */
export function useKeyboardOverlayBox(enabled: boolean = true): {
  height: number;
  offsetTop: number;
  safeTop: number | null;
  /** Soft keyboard currently covering the bottom of the screen (native
   *  plugin OR web visualViewport shrink) — for presentation toggles like
   *  hiding a bottom tab bar or dropping safe-area padding. */
  keyboardOpen: boolean;
} {
  const nativeKeyboard = useKeyboardHeight();
  // Mirror for the visualViewport event handler. Updated in an effect, which
  // still runs before any vv resize event: keyboardWillShow fires (and
  // commits) before the keyboard animation resizes the viewport.
  const nativeKeyboardRef = useRef(0);
  useEffect(() => {
    nativeKeyboardRef.current = nativeKeyboard;
  }, [nativeKeyboard]);

  const [vvHeight, setVvHeight] = useState(0);
  const [offsetTop, setOffsetTop] = useState(0);
  // How far vv.height currently sits below the tallest height seen — the
  // web-side keyboard signal.
  const [webDelta, setWebDelta] = useState(0);
  // visualViewport height last seen while the native keyboard was closed.
  const [baseline, setBaseline] = useState(0);
  const [safeTop, setSafeTop] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const vv = window.visualViewport;
    if (!vv) return;

    // env() can't be read from JS directly; a zero-size probe converts it to
    // a measurable computed padding.
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;top:0;left:0;width:0;height:0;" +
      "padding-top:env(safe-area-inset-top,0px);" +
      "visibility:hidden;pointer-events:none;";
    document.body.appendChild(probe);

    let maxHeight = 0;

    const update = () => {
      const h = vv.height;
      maxHeight = Math.max(maxHeight, h);
      const delta = maxHeight - h;
      setVvHeight(h);
      setOffsetTop(vv.offsetTop);
      setWebDelta(delta);
      if (nativeKeyboardRef.current === 0) {
        setBaseline(h);
        if (delta <= WEB_KEYBOARD_THRESHOLD) {
          // Keyboard fully closed → env() is trustworthy; snapshot it.
          setSafeTop(parseFloat(getComputedStyle(probe).paddingTop) || 0);
        }
      }
    };
    const resetMaxHeight = () => {
      maxHeight = 0;
    };

    // Returning from the background may not fire a viewport resize — re-measure
    // so the overlay goes back to the keyboard-closed height.
    const onVisible = () => {
      if (document.visibilityState === "visible") requestAnimationFrame(update);
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("orientationchange", resetMaxHeight);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("orientationchange", resetMaxHeight);
      document.removeEventListener("visibilitychange", onVisible);
      probe.remove();
    };
  }, [enabled]);

  const keyboardOpen =
    nativeKeyboard > 0 || webDelta > WEB_KEYBOARD_THRESHOLD;

  useEffect(() => {
    if (!enabled || !keyboardOpen) return;
    const reset = () => {
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };
    reset();
    window.addEventListener("scroll", reset);
    return () => window.removeEventListener("scroll", reset);
  }, [enabled, keyboardOpen]);

  // How much of the native keyboard vv.height has already absorbed; subtract
  // only the remainder. vv shrank fully → 0, vv unchanged → full keyboard.
  const alreadyShrunk = Math.max(0, baseline - vvHeight);
  const overlap = Math.max(0, nativeKeyboard - alreadyShrunk);
  const height = vvHeight > 0 ? vvHeight - overlap : 0;

  return { height, offsetTop, safeTop, keyboardOpen };
}
